import { lazy } from "react";

import AuthLayout from "@/components/auth/AuthLayout";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import PublicRoute from "@/components/auth/PublicRoute";
import RootRedirect from "@/components/auth/RootRedirect";
// Business dashboard components
import BusinessLayout from "@/layouts/BusinessLayout";
import MasterLayout from "@/layouts/MasterLayout";
// About pages
import Careers from "@/pages/landing/Careers";
import ComingSoon from "@/pages/landing/ComingSoon";
import ContactUs from "@/pages/landing/ContactUs";
import FAQ from "@/pages/landing/FAQ";
import ForCommunities from "@/pages/landing/ForCommunities";
import Maintenance from "@/pages/landing/Maintenance";
import PrivacyPolicy from "@/pages/landing/PrivacyPolicy";
import ReportIssue from "@/pages/landing/ReportIssue";
import UpdateDetailPage from "@/pages/landing/UpdateDetailPage";
import Updates from "@/pages/landing/Updates";
import WhoWeAre from "@/pages/landing/WhoWeAre";
import Page404 from "@/pages/Status/Page404";
import { type AppRouteObject } from "@/router/interface";
// Lazy load the routes to improve performance
const Home = lazy(() => import("@/pages/landing/Home"));
const Login = lazy(() => import("@/pages/auth/Login"));
const Register = lazy(() => import("@/pages/auth/Register"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const AdminCalendar = lazy(() => import("@/pages/admin/Calendar"));
const AdminUpdates = lazy(() => import("@/pages/admin/Updates"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminDocuments = lazy(() => import("@/pages/admin/Documents"));
const AdminUsers = lazy(() => import("@/pages/admin/Staff"));
const AdminReports = lazy(() => import("@/pages/admin/Reports"));
const AdminCareers = lazy(() => import("@/pages/admin/Careers"));
const AdminSections = lazy(() => import("@/pages/admin/Sections"));
const AdminCommunity = lazy(() => import("@/pages/admin/Community"));
const AdminSocialLinks = lazy(() => import("@/pages/admin/SocialLinksPage"));
const AdminHeroBackgrounds = lazy(() => import("@/pages/admin/HeroBackgrounds"));
const AdminContactInfo = lazy(() => import("@/pages/admin/ContactUs"));
const Subscriptions = lazy(() => import("@/pages/admin/Subscriptions"));

const routes: AppRouteObject[] = [
  // Root redirect - check auth and redirect appropriately
  {
    element: <RootRedirect />,
    path: "/",
  },
  // Home page with MasterLayout (includes all sections)
  {
    children: [
      {
        element: <Home />,
        index: true,
      },
    ],
    element: <MasterLayout />,
    path: "/home",
  },
  // Authentication routes with AuthLayout
  {
    children: [
      {
        element: (
          <PublicRoute>
            <Login />
          </PublicRoute>
        ),
        path: "login",
      },
      {
        element: (
          <ProtectedRoute requireAuth={false}>
            <Register />
          </ProtectedRoute>
        ),
        path: "register",
      },
      {
        element: (
          <ProtectedRoute requireAuth={false}>
            <ForgotPassword />
          </ProtectedRoute>
        ),
        path: "forgot-password",
      },
      {
        element: (
          <ProtectedRoute requireAuth={false}>
            <ResetPassword />
          </ProtectedRoute>
        ),
        path: "reset-password",
      },
    ],
    element: <AuthLayout />,
    path: "/auth",
  },
  {
    element: <FAQ />,
    path: "/support/faq",
  },
  {
    element: <ReportIssue />,
    path: "/support/report-issue",
  },
  {
    element: <WhoWeAre />,
    path: "/pages/about-us/who-we-are",
  },
  {
    element: <Careers />,
    path: "/pages/about-us/careers",
  },
  {
    element: <ForCommunities />,
    path: "/pages/about-us/for-communities",
  },
  {
    element: <Updates />,
    path: "/pages/about-us/updates",
  },
  {
    element: <UpdateDetailPage />,
    path: "/updates/:id",
  },
  {
    element: <ComingSoon />,
    path: "/coming-soon",
  },
  {
    element: <Maintenance />,
    path: "/maintenance",
  },
  {
    element: <ContactUs />,
    path: "/contact-us",
  },
  {
    element: <PrivacyPolicy />,
    path: "/pages/legal/privacy-policy",
  },
  // Admin routes
  {
    children: [
      {
        element: (
          <ProtectedRoute>
            <AdminCalendar />
          </ProtectedRoute>
        ),
        index: true,
      },
      {
        element: (
          <ProtectedRoute>
            <AdminUpdates />
          </ProtectedRoute>
        ),
        path: "updates",
      },
      {
        element: (
          <ProtectedRoute>
            <AdminHeroBackgrounds />
          </ProtectedRoute>
        ),
        path: "hero-backgrounds",
      },
      {
        element: (
          <ProtectedRoute>
            <AdminSocialLinks />
          </ProtectedRoute>
        ),
        path: "social-links",
      },
      {
        element: (
          <ProtectedRoute>
            <AdminSettings />
          </ProtectedRoute>
        ),
        path: "settings",
      },
      {
        element: (
          <ProtectedRoute>
            <AdminDocuments />
          </ProtectedRoute>
        ),
        path: "documents",
      },
      {
        element: (
          <ProtectedRoute>
            <Subscriptions />
          </ProtectedRoute>
        ),
        path: "subscriptions",
      },
      {
        element: (
          <ProtectedRoute>
            <AdminUsers />
          </ProtectedRoute>
        ),
        path: "users",
      },
      {
        element: (
          <ProtectedRoute>
            <AdminSections />
          </ProtectedRoute>
        ),
        path: "sections",
      },
      {
        element: (
          <ProtectedRoute>
            <AdminContactInfo />
          </ProtectedRoute>
        ),
        path: "contact-info",
      },
      {
        element: (
          <ProtectedRoute>
            <AdminReports />
          </ProtectedRoute>
        ),
        path: "reports",
      },
            {
        element: (
          <ProtectedRoute>
            <AdminCommunity />
          </ProtectedRoute>
        ),
        path: "community",
      },
      {
        element: (
          <ProtectedRoute>
            <AdminCareers />
          </ProtectedRoute>
        ),
        path: "careers",
      },
    ],
    element: <BusinessLayout />,
    path: "/admin",
  },
  {
    element: <Page404 />,
    path: "*",
  },
];

export default routes;
